import { NavLink } from 'react-router-dom'
import s from './register.module.scss'
import { FormProvider } from 'react-hook-form';
import { Input } from '@shared/ui/input/input';
import { Button } from '@shared/ui/button/button';
import { ConsentBlock, ConsentCaption } from '@shared/ui/consent-block/consent-block';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import { ActionsWrapper, AuthAction } from '../actions-wrapper/actions-wrapper';

const errors = {
	email: 'Почта уже используется',
	phone: 'Телефон уже используется',
}

const fieldHints = {
	name: 'Введите имя с заглавной буквы, без пробелов и цифр. Двойные имена через дефис. Например: Иван или Анна-Мария.',
	email: 'Укажите почту, к которой у вас есть доступ. На нее могут приходить важные уведомления.',
	phone: 'Введите номер в российском формате. Например: +7(999) 999-99-99.',
}

const fieldValidators = {
	name: (value = '') => value.length >= 2 && value.length <= 255 && /^[А-ЯЁ][а-яё]*(?:-[А-ЯЁ][а-яё]*)*$/.test(value),
	email: (value = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
	phone: (value = '') => /^\+\d\(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(value),
}

const passwordRequirements = [
	{
		label: 'от 8 до 50 символов',
		isValid: (value = '') => value.length >= 8 && value.length <= 50,
	},
	{
		label: 'есть латинская буква',
		isValid: (value = '') => /[a-zA-Z]/.test(value),
	},
	{
		label: 'есть цифра',
		isValid: (value = '') => /\d/.test(value),
	},
	{
		label: 'нет пробелов',
		isValid: (value = '') => !/\s/.test(value),
	},
]

const FieldWithHint = ({ hint, isValid, children }) => (
	<div className={`${s.fieldHintWrapper} ${isValid ? s.fieldValid : ''}`}>
		{children}
		{!isValid && (
			<div className={s.fieldHint} role="tooltip">
				{hint}
			</div>
		)}
	</div>
)

const ValidIcon = () => <IconCircleCheckFilled className={s.validIcon} size={20} />

const PasswordHint = ({ password = '' }) => (
	<div className={s.passwordHint}>
		<p>Пароль должен содержать:</p>
		<ul>
			{passwordRequirements.map(({ label, isValid }) => (
				<li key={label} className={isValid(password) ? s.requirementDone : ''}>
					{label}
				</li>
			))}
		</ul>
	</div>
)

export const Register = ({handleSubmit, referral, error, methods, formState, role}) => {
	const name = methods?.watch('name')
	const email = methods?.watch('email')
	const phone = methods?.watch('phone')
	const password = methods?.watch('password')
	const personalDataConsent = methods?.watch('personal_data_consent')
	const formFull = name && email && phone && password
	const isNameValid = fieldValidators.name(name)
	const isEmailValid = fieldValidators.email(email)
	const isPhoneValid = fieldValidators.phone(phone)
	const isPasswordValid = passwordRequirements.every(({ isValid }) => isValid(password))

	const submitting = formState.isSubmitting
	const notValid = (!formState.isValid && formState.isDirty) || !formFull || !personalDataConsent || submitting
	const isTeacher = role === 'teacher'
	const studentSignUpLink = referral ? `/sign-up/${referral}` : '/sign-up'
	const teacherSignUpLink = referral ? `/sign-up-teacher/${referral}` : '/sign-up-teacher'
	
	return (
    <div className={s.wrapper}>
			<div className={s.card}>
				{/* <NavLink to={'/'} className={s.logo}>
					<img src={process.env.REACT_APP_STORAGE_URL + "images/logo.svg"} alt="Созвучно - сервис по поиску преподавателей музыки"/>
				</NavLink> */}
				<h2 className={s.title}>
					<span className={s.role}>{isTeacher ? 'Преподаватель' : 'Ученик'}</span>
					Регистрация
				</h2>
				<ActionsWrapper>
					<AuthAction>
						Уже есть аккаунт?
						<NavLink to={'/login'}> Войти</NavLink>
					</AuthAction>
					{!isTeacher && (
						<AuthAction>
							Вы хотите преподавать?
							<NavLink to={teacherSignUpLink}> Заполнить анкету</NavLink>
						</AuthAction>
					)}
					{isTeacher && (
						<AuthAction>
							Хотите заниматься музыкой?
							<NavLink to={studentSignUpLink}> Зарегистрироваться учеником</NavLink>
						</AuthAction>
					)}
				</ActionsWrapper>
				<FormProvider {...methods}>
					{error?.localMessage && <span className={s.error}>{error.localMessage}</span>}
					{error?.data?.errors && Object.keys(error?.data?.errors).map(err => <span key={err} className={s.error}>{errors[err] || error.data.errors[err]}</span>)}
					<form onSubmit={handleSubmit} className={s.form}>
						<FieldWithHint hint={fieldHints.name} isValid={isNameValid}>
							<Input
								placeholder='Иван'
								name='name'
								className={s.input}
								title='Ваше имя'
								disabled={submitting}
								rightIcon={isNameValid && <ValidIcon />}
								validation={{
									required: {
										value: true,
										message: 'Заполните поле',
									},
									minLength: {
										value: 2,
										message: 'Минимум 2 символов',
									},
									maxLength: {
										value: 255,
										message: 'Максимум 255 символов',
									},
									validate: {
										matchPattern: (v) =>
										/^[А-ЯЁ][а-яё]*(?:-[А-ЯЁ][а-яё]*)*$/.test(v) ||
										"Имя заполнено не верно",
									},
							}}/> 
						</FieldWithHint>
						<FieldWithHint hint={fieldHints.email} isValid={isEmailValid}>
							<Input
								type="email"
								placeholder='example@mail.ru'
								name='email'
								className={s.input}
								title='Электронная почта'
								disabled={submitting}
								rightIcon={isEmailValid && <ValidIcon />}
								validation={{
									required: {
										value: true,
										message: 'Почта обязательна',
									},
									pattern: {
										value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
										message: 'Неверный формат почты',
									},
							}}/> 
						</FieldWithHint>
						<FieldWithHint hint={fieldHints.phone} isValid={isPhoneValid}>
							<Input
								placeholder='+7(999) 999 99-99'
								name='phone'
								className={s.input}
								title='Ваш телефон'
								mask="+9(999) 999-99-99"
								autoComplete="username"
								disabled={submitting}
								rightIcon={isPhoneValid && <ValidIcon />}
								validation={{
									required: 'Телефон обязателен',
									pattern: {
										value: /^\+\d\(\d{3}\) \d{3}-\d{2}-\d{2}$/,
										message: 'Неверный формат телефона',
									},
							}}/> 
						</FieldWithHint>
						<FieldWithHint hint={<PasswordHint password={password} />} isValid={isPasswordValid}>
							<Input
								type="password"
								autoComplete="new-password"
								placeholder='Пароль' name="password"
								className={s.input}
								title='Пароль'
								disabled={submitting}
								rightIcon={isPasswordValid && <ValidIcon />}
								validation={{
									required: {
										value: true,
										message: 'Поле незаполнено',
									},
									validate: {
										length: (v) => passwordRequirements[0].isValid(v) || 'Пароль должен быть от 8 до 50 символов',
										letter: (v) => passwordRequirements[1].isValid(v) || 'Добавьте латинскую букву',
										number: (v) => passwordRequirements[2].isValid(v) || 'Добавьте цифру',
										spaces: (v) => passwordRequirements[3].isValid(v) || 'Уберите пробелы',
									},
							}}/> 
						</FieldWithHint>
						<div className={s.checkboxes}>
							<ConsentBlock className={s.consentBlock} showUserAgreementCaption={false}/>
						</div>
						<Button label={isTeacher ? 'Отправить анкету' : 'Создать аккаунт'} className={s.button} disabled={notValid} fetching={submitting}/>
						<ConsentCaption className={s.consentCaption}/>
					</form>
				</FormProvider>
			</div>
		</div>
    );
}
